import api from "../../../../lib/api";
import { Input } from "../../../../components/ui/input";
import { Sheet, SheetContent, SheetCloseX, SheetHeader, SheetTitle, SheetDescription } from "../../../../components/ui/sheet";
import { toast } from "sonner";
import { Plus } from "lucide-react";

/**
 * PlaybooksSheet — right-side sheet + inline editor for saved
 * shipping plans (Option A · Pt 2).
 *
 * The editor lives INSIDE the SheetContent (list ↔ editor toggle)
 * rather than in a nested fixed-position dialog — otherwise Radix's
 * Dialog overlay hijacks pointer-events on child inputs.
 */
export default function PlaybooksSheet({
  open, onOpenChange,
  playbooks, setPlaybooks,
  playbookDraft, setPlaybookDraft,
  selected, reload, settings,
  playbookBusy, setPlaybookBusy,
}) {
  const applyToSelected = async (p) => {
    if (selected.size === 0) return toast.error("Select orders first");
    setPlaybookBusy(true);
    try {
      const { data } = await api.post(`/dropship/playbooks/${p.id}/apply`, { order_ids: Array.from(selected) });
      toast.success(`Applied to ${data.modified} order${data.modified === 1 ? "" : "s"}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setPlaybookBusy(false); }
  };

  const deletePlaybook = async (p) => {
    if (!window.confirm(`Delete playbook "${p.name}"?`)) return;
    try {
      await api.delete(`/dropship/playbooks/${p.id}`);
      const { data } = await api.get("/dropship/playbooks");
      setPlaybooks(data?.playbooks || []);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const savePlaybook = async () => {
    if (!playbookDraft.name?.trim()) return toast.error("Name required");
    const body = {
      name: playbookDraft.name.trim(),
      description: playbookDraft.description || "",
      profile_id: playbookDraft.profile_id || null,
      carrier: playbookDraft.carrier || null,
      service_token: playbookDraft.service_token || null,
      ship_from_address_id: playbookDraft.ship_from_address_id || null,
      add_insurance: !!playbookDraft.add_insurance,
      min_insurance: playbookDraft.min_insurance || null,
      signature_required: !!playbookDraft.signature_required,
      saturday_delivery: !!playbookDraft.saturday_delivery,
      tags: playbookDraft.tags || [],
    };
    try {
      if (playbookDraft.id) {
        await api.put(`/dropship/playbooks/${playbookDraft.id}`, body);
      } else {
        await api.post("/dropship/playbooks", body);
      }
      const { data } = await api.get("/dropship/playbooks");
      setPlaybooks(data?.playbooks || []);
      setPlaybookDraft(null);
      toast.success(`Playbook ${playbookDraft.id ? "updated" : "created"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setPlaybookDraft(null); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="playbooks-sheet">
        <SheetCloseX/>
        <SheetHeader>
          <SheetTitle>{playbookDraft ? (playbookDraft.id ? "Edit playbook" : "New playbook") : "Playbooks"}</SheetTitle>
          <SheetDescription>
            {playbookDraft
              ? "Playbooks bundle a set of shipping preferences you can apply to any batch in one click."
              : "Save a bundle of shipping preferences (profile, carrier, insurance, warehouse, tags) and apply it to any batch in one click. Playbook hints beat the seller-wide default carrier but lose to any matched automation rule."}
          </SheetDescription>
        </SheetHeader>

        {/* List mode */}
        {!playbookDraft && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[13px] font-medium">{playbooks.length} playbook{playbooks.length === 1 ? "" : "s"}</div>
              <button
                onClick={() => setPlaybookDraft({
                  name: "", description: "", profile_id: "", carrier: "",
                  service_token: "", ship_from_address_id: "",
                  add_insurance: false, min_insurance: 0,
                  signature_required: false, saturday_delivery: false,
                  tags: [],
                })}
                data-testid="add-playbook-button"
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
                <Plus className="w-3 h-3"/>New playbook
              </button>
            </div>
            {playbooks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-6 text-center text-[12px] text-foreground/55">
                No playbooks yet. Try &ldquo;VIP Overnight&rdquo; (UPS Next Day + signature) or &ldquo;Bulk Freight&rdquo; (LTL + saturday delivery).
              </div>
            ) : (
              <div className="space-y-2">
                {playbooks.map(p => (
                  <div key={p.id} className="rounded-xl border border-border bg-white p-3" data-testid={`playbook-${p.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium truncate">{p.name}</div>
                        <div className="text-[11px] text-foreground/55 truncate">
                          {[
                            p.carrier ? `carrier: ${p.carrier}` : "",
                            p.service_token ? `svc: ${p.service_token}` : "",
                            p.add_insurance ? "insure" : "",
                            p.signature_required ? "signature" : "",
                            (p.tags || []).length ? `tags: ${(p.tags || []).join(", ")}` : "",
                          ].filter(Boolean).join(" · ") || "no hints"}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          disabled={selected.size === 0 || playbookBusy}
                          onClick={() => applyToSelected(p)}
                          data-testid={`playbook-apply-${p.id}`}
                          className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40">
                          Apply · {selected.size}
                        </button>
                        <button
                          onClick={() => setPlaybookDraft({ ...p })}
                          data-testid={`playbook-edit-${p.id}`}
                          className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border border-border hover:bg-secondary">
                          Edit
                        </button>
                        <button
                          onClick={() => deletePlaybook(p)}
                          data-testid={`playbook-delete-${p.id}`}
                          className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border border-border hover:bg-rose-50 hover:border-rose-200 text-rose-600">
                          Delete
                        </button>
                      </div>
                    </div>
                    {p.description && (
                      <div className="text-[11px] text-foreground/60 mt-1.5">{p.description}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Editor mode (inline inside sheet — avoids z-index / pointer-events fights with Radix overlay) */}
        {playbookDraft && (
          <div className="mt-4 space-y-4" data-testid="playbook-editor-dialog">
            <Input placeholder="Playbook name" value={playbookDraft.name || ""}
              onChange={e => setPlaybookDraft(d => ({ ...d, name: e.target.value }))}
              className="h-10 font-medium" data-testid="playbook-name"/>
            <Input placeholder="Description (optional)" value={playbookDraft.description || ""}
              onChange={e => setPlaybookDraft(d => ({ ...d, description: e.target.value }))}
              className="h-10" data-testid="playbook-description"/>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Profile</label>
                <select value={playbookDraft.profile_id || ""}
                  onChange={e => setPlaybookDraft(d => ({ ...d, profile_id: e.target.value }))}
                  data-testid="playbook-profile"
                  className="w-full h-10 rounded-lg border border-border bg-white px-2 text-[13px]">
                  <option value="">Default</option>
                  {(settings.shipping_profiles || []).map(pr => (
                    <option key={pr.id} value={pr.id}>{pr.name} · {pr.weight_lb}lb</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Warehouse</label>
                <select value={playbookDraft.ship_from_address_id || ""}
                  onChange={e => setPlaybookDraft(d => ({ ...d, ship_from_address_id: e.target.value }))}
                  data-testid="playbook-warehouse"
                  className="w-full h-10 rounded-lg border border-border bg-white px-2 text-[13px]">
                  <option value="">Default</option>
                  {(settings.ship_from_addresses || []).map(w => (
                    <option key={w.id} value={w.id}>{w.name || w.street1}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Carrier</label>
                <select value={playbookDraft.carrier || ""}
                  onChange={e => setPlaybookDraft(d => ({ ...d, carrier: e.target.value }))}
                  data-testid="playbook-carrier"
                  className="w-full h-10 rounded-lg border border-border bg-white px-2 text-[13px]">
                  <option value="">Any (rate-shop)</option>
                  <option value="usps">USPS</option>
                  <option value="ups">UPS</option>
                  <option value="fedex">FedEx</option>
                  <option value="dhl_express">DHL Express</option>
                </select>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Service token</label>
                <Input value={playbookDraft.service_token || ""}
                  onChange={e => setPlaybookDraft(d => ({ ...d, service_token: e.target.value }))}
                  placeholder="ups_ground"
                  className="h-10 mono"/>
              </div>
            </div>
            <div className="rounded-xl border border-border p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!playbookDraft.add_insurance}
                  onChange={e => setPlaybookDraft(d => ({ ...d, add_insurance: e.target.checked }))}
                  data-testid="playbook-insurance"
                  className="w-4 h-4 accent-foreground"/>
                <span className="text-[13px]">Auto-add insurance</span>
              </label>
              {playbookDraft.add_insurance && (
                <Input type="number" min="0" step="10" placeholder="Min insurance $"
                  value={playbookDraft.min_insurance || ""}
                  onChange={e => setPlaybookDraft(d => ({ ...d, min_insurance: Number(e.target.value || 0) }))}
                  className="h-10 mono w-40"/>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!playbookDraft.signature_required}
                  onChange={e => setPlaybookDraft(d => ({ ...d, signature_required: e.target.checked }))}
                  data-testid="playbook-signature"
                  className="w-4 h-4 accent-foreground"/>
                <span className="text-[13px]">Require signature on delivery</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!playbookDraft.saturday_delivery}
                  onChange={e => setPlaybookDraft(d => ({ ...d, saturday_delivery: e.target.checked }))}
                  className="w-4 h-4 accent-foreground"/>
                <span className="text-[13px]">Saturday delivery (where supported)</span>
              </label>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Tags (comma-separated)</label>
              <Input value={(playbookDraft.tags || []).join(", ")}
                onChange={e => setPlaybookDraft(d => ({ ...d, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))}
                placeholder="vip, weekend, heavy"
                data-testid="playbook-tags"
                className="h-10 mono"/>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button onClick={() => setPlaybookDraft(null)}
                data-testid="playbook-cancel"
                className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
                Cancel
              </button>
              <button
                onClick={savePlaybook}
                data-testid="playbook-save"
                className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90">
                {playbookDraft.id ? "Save" : "Create"}
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
